import i18n from "@/i18n";
import { invokeWithRetry } from "./common/invokeWithRetry";

// ============================================================================
// Wrapper de l'edge function `accounting-management` (module comptabilité).
// Pattern paymentsManagementWrapper : retour uniforme { data, error }.
// Règle d'or `authExpired` : en LECTURE on renvoie une donnée vide sans
// erreur (la déconnexion est déjà en cours) ; en ÉCRITURE on renvoie
// IMPÉRATIVEMENT une erreur — sinon l'UI annoncerait un succès pour une
// écriture jamais partie.
// ============================================================================

export type ExpenseKind = "expense" | "supplier_invoice" | "salary";
export type ExpenseCategory =
  | "rent" | "salaries" | "suppliers" | "equipment" | "marketing"
  | "maintenance" | "utilities" | "insurance" | "other";
export type ExpenseStatus = "due" | "paid" | "cancelled";
export type ExpenseMethod = "cash" | "card" | "bank_transfer" | "check";

export interface Expense {
  id: string;
  gym_id: string;
  kind: ExpenseKind;
  category: ExpenseCategory;
  label: string;
  amount: number;
  expense_date: string;
  status: ExpenseStatus;
  payment_method: ExpenseMethod | null;
  paid_at: string | null;
  due_date: string | null;
  supplier_id: string | null;
  invoice_number: string | null;
  employee_name: string | null;
  coach_id: string | null;
  is_template: boolean;
  recurrence: "monthly" | null;
  recurring_source_id: string | null;
  period_month: string | null;
  attachment_path: string | null;
  notes: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  suppliers?: { id: string; name: string } | null;
}

export interface Supplier {
  id: string;
  gym_id: string;
  name: string;
  contact_name: string | null;
  phone: string | null;
  email: string | null;
  notes: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CashflowSummary {
  cash_in: number;
  cash_out: number;
  net: number;
  pending_in: number;
  due_out: number;
  byCategory: Record<string, number>;
  byMethod: { in: Record<string, number>; out: Record<string, number> };
}

export interface CashflowMonth {
  month: string; // yyyy-MM
  cash_in: number;
  cash_out: number;
  net: number;
}

export interface CashflowData {
  summary: CashflowSummary;
  monthly: CashflowMonth[];
  invoiced: { month: string; amount: number }[];
  period: { start_date: string; end_date: string };
}

export type UpcomingExpense = Pick<Expense, "id" | "kind" | "category" | "label" | "amount" | "due_date" | "expense_date" | "supplier_id"> & {
  suppliers?: { name: string } | null;
};

export interface UpcomingData {
  /** Dues dans la fenêtre [aujourd'hui, horizon]. */
  expenses_due: UpcomingExpense[];
  /** Arriéré : dues avant aujourd'hui, hors timeline projetée. */
  expenses_overdue: UpcomingExpense[];
  schedules_due: {
    id: string;
    subscription_id: string;
    installment_number: number;
    due_date: string;
    amount: number;
    status: string;
  }[];
  horizon_days: number;
}

const sessionExpiredError = () =>
  new Error(i18n.t("common:errors.sessionExpired", "Session expirée — reconnectez-vous."));

async function call(action: string, params: Record<string, unknown>) {
  return invokeWithRetry("accounting-management", { action, ...params });
}

// ------------------------------------------------------------------ lectures

export interface ExpenseFiltersParams {
  gym_id: string;
  kind?: ExpenseKind;
  category?: ExpenseCategory;
  status?: ExpenseStatus;
  supplier_id?: string;
  start_date?: string;
  end_date?: string;
  search?: string;
  include_templates?: boolean;
  /** true : renvoie UNIQUEMENT les modèles récurrents. */
  templates_only?: boolean;
}

export async function listExpenses(params: ExpenseFiltersParams) {
  const { data, error, authExpired } = await call("list_expenses", { ...params });
  if (authExpired) return { data: [] as Expense[], error: null };
  if (error) return { data: null, error };
  return { data: (data?.data ?? []) as Expense[], error: null };
}

export async function listSuppliers(gym_id: string, include_inactive = false) {
  const { data, error, authExpired } = await call("list_suppliers", { gym_id, include_inactive });
  if (authExpired) return { data: [] as Supplier[], error: null };
  if (error) return { data: null, error };
  return { data: (data?.data ?? []) as Supplier[], error: null };
}

export async function getCashflow(params: {
  gym_id: string;
  start_date?: string;
  end_date?: string;
  months?: number;
}) {
  const { data, error, authExpired } = await call("get_cashflow", params);
  if (authExpired) return { data: null, error: null };
  if (error) return { data: null, error };
  return { data: data?.data as CashflowData, error: null };
}

export async function getUpcoming(
  gym_id: string,
  period?: { start_date: string; end_date: string }
) {
  const { data, error, authExpired } = await call("get_upcoming", { gym_id, ...(period ?? {}) });
  if (authExpired) return { data: null, error: null };
  if (error) return { data: null, error };
  return { data: data?.data as UpcomingData, error: null };
}

// ----------------------------------------------------------------- écritures

async function write(action: string, params: Record<string, unknown>) {
  const { data, error, authExpired } = await call(action, params);
  if (authExpired) return { data: null, error: sessionExpiredError() };
  if (error) return { data: null, error };
  return { data, error: null };
}

export async function createExpense(params: {
  gym_id: string;
  kind: ExpenseKind;
  category: ExpenseCategory;
  label: string;
  amount: number;
  expense_date: string;
  status?: "due" | "paid";
  payment_method?: ExpenseMethod;
  paid_at?: string;
  due_date?: string;
  supplier_id?: string;
  invoice_number?: string;
  employee_name?: string;
  coach_id?: string;
  recurrence?: "monthly";
  notes?: string;
}) {
  const res = await write("create_expense", params);
  return { data: (res.data?.data ?? null) as Expense | null, error: res.error };
}

export async function updateExpense(id: string, gym_id: string, patch: Partial<Omit<Expense, "id" | "gym_id" | "created_by" | "created_at" | "updated_at">>) {
  const res = await write("update_expense", { id, gym_id, ...patch });
  return { data: (res.data?.data ?? null) as Expense | null, error: res.error };
}

export async function deleteExpense(id: string, gym_id: string) {
  const res = await write("delete_expense", { id, gym_id });
  return { data: res.error ? null : { success: true }, error: res.error };
}

export async function markExpensePaid(id: string, gym_id: string, opts?: { payment_method?: ExpenseMethod; paid_at?: string }) {
  const res = await write("mark_paid", { id, gym_id, ...opts });
  return { data: (res.data?.data ?? null) as Expense | null, error: res.error };
}

export async function createSupplier(gym_id: string, supplier: { name: string; contact_name?: string; phone?: string; email?: string; notes?: string }) {
  const res = await write("create_supplier", { gym_id, ...supplier });
  return { data: (res.data?.data ?? null) as Supplier | null, error: res.error };
}

export async function updateSupplier(id: string, gym_id: string, patch: Partial<Pick<Supplier, "name" | "contact_name" | "phone" | "email" | "notes" | "is_active">>) {
  const res = await write("update_supplier", { id, gym_id, ...patch });
  return { data: (res.data?.data ?? null) as Supplier | null, error: res.error };
}

export async function deleteSupplier(id: string, gym_id: string) {
  const res = await write("delete_supplier", { id, gym_id });
  return { data: res.error ? null : { soft: !!res.data?.soft }, error: res.error };
}

export async function generateRecurring(gym_id: string, month?: string) {
  const res = await write("generate_recurring", { gym_id, month });
  return {
    data: res.error ? null : (res.data as { month: string; templates: number; created: number }),
    error: res.error,
  };
}
